// frontend/src/features/campaign/Campaign.js
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Banner from '../../ui/Banner';
import axios from '../../api/axios';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAuth } from '../../contexts/KeycloakContext';
import CampaignRoleBanner from './components/common/CampaignRoleBanner';
import CampaignListView from './components/list/CampaignListView';
import CreateCampaign from './components/create/CreateCampaign';
import { STATUS_OPTIONS } from './constants/campaignConstants';
import { INITIAL_CAMPAIGNS } from './data/initialCampaignData';
import './Campaign.css';

export default function Campaign() {
  const {
    isOwner,
    isOrganizationAdmin,
    isSalesManager,
    isSalesMember,
    isResearcher,
    isViewer,
    isOrgMember,
    isPersonalWorkspace,
    isPersonalUser,
    isSuperadmin,
    isPlatformAdmin,
    workspaceName,
    workspaceType,
    workspaceId,
    displayRole,
    username,
  } = useWorkspace();

  const { keycloak } = useAuth();

  // ── View Mode: 'LIST' | 'CREATE' | 'DETAIL' ──
  const [viewMode, setViewMode] = useState('LIST');
  const [selectedPlaybook, setSelectedPlaybook] = useState(null);
  const [selectedCampaignForDetail, setSelectedCampaignForDetail] = useState(null);

  // ── Role Authorization Check ──
  const isManagementUser = useMemo(() => {
    if (isPersonalWorkspace || isPersonalUser || !workspaceType || workspaceType === 'personal') {
      return true;
    }
    return isOwner || isOrganizationAdmin || isSalesManager || isSuperadmin || isPlatformAdmin;
  }, [
    isPersonalWorkspace,
    isPersonalUser,
    workspaceType,
    isOwner,
    isOrganizationAdmin,
    isSalesManager,
    isSuperadmin,
    isPlatformAdmin,
  ]);

  const canDirectApprove = isManagementUser;

  const userRoleLabel = useMemo(() => {
    if (isPersonalWorkspace || isPersonalUser) return 'Personal Workspace Owner';
    if (isOwner) return 'Workspace Owner';
    if (isOrganizationAdmin) return 'Organization Admin';
    if (isSalesManager) return 'Sales Manager';
    if (isSalesMember || isOrgMember) return 'Sales Member';
    if (isResearcher) return 'Data Researcher';
    if (isViewer) return 'Viewer';
    return displayRole || 'Team Member';
  }, [
    isPersonalWorkspace,
    isPersonalUser,
    isOwner,
    isOrganizationAdmin,
    isSalesManager,
    isSalesMember,
    isResearcher,
    isViewer,
    isOrgMember,
    displayRole,
  ]);

  const currentUserName = useMemo(() => {
    return keycloak?.tokenParsed?.name || username || 'Sales Strategist';
  }, [keycloak, username]);

  const roleAccessText = useMemo(() => {
    if (isPersonalWorkspace || isPersonalUser) {
      return 'You have direct permissions to create, approve, and launch campaigns for your workspace.';
    }
    if (isOwner) {
      return 'You have full administrative access to create, approve, and launch all campaigns in this workspace.';
    }
    if (isOrganizationAdmin) {
      return 'You have organization administrative permissions to manage, approve, and activate all campaigns in this workspace.';
    }
    if (isSalesManager) {
      return 'You can assign leads, create campaign strategies, and directly approve all campaigns in this workspace.';
    }
    if (isSalesMember || isOrgMember) {
      return 'You can design campaign strategies, view campaigns you created, and submit them for executive approval.';
    }
    if (isResearcher) {
      return 'You can view and analyze data for campaigns created by you in this workspace.';
    }
    if (isViewer) {
      return 'You have read-only access to campaigns created by you in this workspace.';
    }
    return 'You can view campaigns created by you in this workspace.';
  }, [
    isPersonalWorkspace,
    isPersonalUser,
    isOwner,
    isOrganizationAdmin,
    isSalesManager,
    isSalesMember,
    isResearcher,
    isViewer,
    isOrgMember,
  ]);

  const [campaigns, setCampaigns] = useState([]);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [isSeedingSamples, setIsSeedingSamples] = useState(false);
  const [successBannerMessage, setSuccessBannerMessage] = useState('');
  const [loadError, setLoadError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('ALL');
  const [showBlueprints, setShowBlueprints] = useState(false);
  const [initialWizardStep, setInitialWizardStep] = useState(1);

  // ── Database Sync: Fetch campaigns for active workspace ──
  const fetchCampaigns = useCallback(async () => {
    if (!keycloak?.authenticated) return;
    setIsLoadingCampaigns(true);
    setLoadError('');
    try {
      const res = await axios.get('/campaign');
      if (res.data?.success && Array.isArray(res.data.data)) {
        setCampaigns(res.data.data);
      }
    } catch (err) {
      console.error('[Campaign] Failed to fetch campaigns from database:', err);
      // Fallback: If network/server fails, maintain empty or existing state
      setLoadError(
        err.response?.data?.error || err.message || 'Failed to load campaigns from database'
      );
    } finally {
      setIsLoadingCampaigns(false);
    }
  }, [keycloak?.authenticated]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns, workspaceId]);

  // ── Sync with CRM Lead Deletions (Real-time) ──
  useEffect(() => {
    const handleLeadChanged = (e) => {
      const data = e?.detail;
      if (data?.action === 'deleted') {
        const deletedIds = new Set(
          [data?.leadId, ...(Array.isArray(data?.leadIdentifiers) ? data.leadIdentifiers : [])]
            .filter(Boolean)
            .map(String)
        );
        if (deletedIds.size === 0) return;

        setCampaigns((prev) =>
          prev.map((c) => {
            const imported = c.importedLeads || [];
            const target = c.targetLeads || [];
            const stratImported = c.strategy?.importedLeads || [];
            const stratTarget = c.strategy?.targetLeads || [];
            const matches = (l) => {
              const ids = [l?.id, l?.shared_item_id, l?.lead_id, l?.leadId, l?.external_id]
                .filter(Boolean)
                .map(String);
              return ids.some((i) => deletedIds.has(i));
            };
            const newImported = imported.filter((l) => !matches(l));
            const newTarget = target.filter((l) => !matches(l));
            const newStratImported = stratImported.filter((l) => !matches(l));
            const newStratTarget = stratTarget.filter((l) => !matches(l));
            const changed =
              newImported.length !== imported.length ||
              newTarget.length !== target.length ||
              newStratImported.length !== stratImported.length ||
              newStratTarget.length !== stratTarget.length;
            if (!changed) return c;
            return {
              ...c,
              importedLeads: newImported,
              targetLeads: newTarget,
              audienceCount: newImported.length > 0 ? newImported.length : c.audienceCount,
              strategy: {
                ...c.strategy,
                importedLeads: newStratImported,
                targetLeads: newStratTarget,
              },
            };
          })
        );
      } else if (data?.action === 'updated') {
        const updatedIds = new Set(
          [data?.leadId, ...(Array.isArray(data?.leadIdentifiers) ? data.leadIdentifiers : [])]
            .filter(Boolean)
            .map(String)
        );
        if (updatedIds.size === 0) return;

        const newStage = data?.stage;
        if (!newStage) return;

        const isNewActive = String(newStage).toLowerCase() === 'new';

        const updateLeadObj = (l) => {
          const ids = [l?.id, l?.shared_item_id, l?.lead_id, l?.leadId, l?.external_id]
            .filter(Boolean)
            .map(String);
          if (ids.some((i) => updatedIds.has(i))) {
            return {
              ...l,
              stage: newStage,
              ...(isNewActive ? { leadResponse: 'no_reply' } : {}),
            };
          }
          return l;
        };

        const updateCampaignRecord = (c) => {
          if (!c) return c;
          const imported = c.importedLeads || [];
          const target = c.targetLeads || [];
          const stratImported = c.strategy?.importedLeads || [];
          const stratTarget = c.strategy?.targetLeads || [];

          const matchCheck = (l) => {
            const ids = [l?.id, l?.shared_item_id, l?.lead_id, l?.leadId, l?.external_id]
              .filter(Boolean)
              .map(String);
            return ids.some((i) => updatedIds.has(i));
          };

          const hasMatches =
            imported.some(matchCheck) ||
            target.some(matchCheck) ||
            stratImported.some(matchCheck) ||
            stratTarget.some(matchCheck);

          if (!hasMatches) return c;

          let nextJourney = c.journey;
          if (isNewActive && Array.isArray(c.journey)) {
            nextJourney = c.journey.map((step) => {
              if (!step?.leadResponses) return step;
              let stepChanged = false;
              const nextResp = { ...step.leadResponses };
              Object.keys(nextResp).forEach((k) => {
                if (updatedIds.has(String(k))) {
                  delete nextResp[k];
                  stepChanged = true;
                }
              });
              return stepChanged ? { ...step, leadResponses: nextResp } : step;
            });
          }

          return {
            ...c,
            importedLeads: imported.map(updateLeadObj),
            targetLeads: target.map(updateLeadObj),
            journey: nextJourney,
            strategy: {
              ...(c.strategy || {}),
              importedLeads: stratImported.map(updateLeadObj),
              targetLeads: stratTarget.map(updateLeadObj),
              journey: nextJourney,
            },
          };
        };

        setCampaigns((prev) => prev.map(updateCampaignRecord));
        setSelectedCampaignForDetail((prev) => (prev ? updateCampaignRecord(prev) : prev));
      }
    };
    window.addEventListener('lead-changed', handleLeadChanged);
    return () => window.removeEventListener('lead-changed', handleLeadChanged);
  }, []);

  // ── Filter campaigns by creator for non-management roles (Member, Viewer, Researcher) ──
  const userVisibleCampaigns = useMemo(() => {
    if (isManagementUser) return campaigns;
    const currentName = (currentUserName || '').toLowerCase().trim();
    const currentUname = (username || '').toLowerCase().trim();
    const currentSub = keycloak?.tokenParsed?.sub;

    return campaigns.filter((c) => {
      // 1. Match by createdByUserId
      if (c.createdByUserId && currentSub && String(c.createdByUserId) === String(currentSub)) {
        return true;
      }
      // 2. Match by createdBy string
      if (c.createdBy) {
        const createdLower = c.createdBy.toLowerCase();
        if (currentName && createdLower.includes(currentName)) return true;
        if (currentUname && createdLower.includes(currentUname)) return true;
      }
      return false;
    });
  }, [campaigns, isManagementUser, currentUserName, username, keycloak]);

  // Status counts based on user-accessible campaigns
  const statusCounts = useMemo(() => {
    const counts = { ALL: userVisibleCampaigns.length };
    STATUS_OPTIONS.forEach((opt) => {
      if (opt.key === 'PAUSE_REQUESTED') {
        counts[opt.key] = userVisibleCampaigns.filter(
          (c) =>
            c.status === 'ACTIVE' && Boolean(c.pauseRequestedBy || c.governance?.pauseRequestedBy)
        ).length;
      } else if (opt.key !== 'ALL') {
        counts[opt.key] = userVisibleCampaigns.filter((c) => c.status === opt.key).length;
      }
    });
    return counts;
  }, [userVisibleCampaigns]);

  // Filtered campaigns
  const filteredCampaigns = useMemo(() => {
    return userVisibleCampaigns.filter((c) => {
      if (selectedStatus === 'PAUSE_REQUESTED') {
        if (
          c.status !== 'ACTIVE' ||
          !Boolean(c.pauseRequestedBy || c.governance?.pauseRequestedBy)
        ) {
          return false;
        }
      } else if (selectedStatus !== 'ALL' && c.status !== selectedStatus) {
        return false;
      }

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesName = c.name?.toLowerCase().includes(q);
        const matchesObjective = c.objective?.toLowerCase().includes(q);
        const matchesProduct = c.product?.toLowerCase().includes(q);
        const matchesMarket = c.targetMarket?.toLowerCase().includes(q);
        const matchesPersona = c.targetPersonas?.some((p) => p.toLowerCase().includes(q));

        return matchesName || matchesObjective || matchesProduct || matchesMarket || matchesPersona;
      }

      return true;
    });
  }, [userVisibleCampaigns, selectedStatus, searchQuery]);

  // ── CRUD Handlers wired to PostgreSQL Database ──
  const handleCreateCampaign = async (newCampaignData, actionType) => {
    const res = await axios.post('/campaign', newCampaignData);
    if (res.data?.success && res.data.data) {
      const created = res.data.data;
      setCampaigns((prev) => [created, ...prev]);
      setSelectedPlaybook(null);

      const firstTouchpoint =
        (newCampaignData.strategy?.journey && newCampaignData.strategy.journey[0]) ||
        (newCampaignData.journey && newCampaignData.journey[0]);
      const rawChannel = (
        firstTouchpoint?.channel ||
        (Array.isArray(newCampaignData.channels) &&
          newCampaignData.channels.find((c) => /linkedin|whatsapp|email/i.test(c))) ||
        'email'
      ).toLowerCase();
      const needsOutreachModal =
        actionType === 'DIRECT_ACTIVATE' &&
        (rawChannel.includes('email') ||
          rawChannel.includes('whatsapp') ||
          rawChannel.includes('linkedin'));

      if (!needsOutreachModal) {
        setViewMode('LIST');
      }

      setSuccessBannerMessage(
        `Campaign "${created.name}" successfully created and saved to database.`
      );
      setTimeout(() => setSuccessBannerMessage(''), 6000);
      return created;
    } else {
      throw new Error(res.data?.error || res.data?.message || 'Failed to save campaign');
    }
  };

  const handleUpdateCampaign = async (updatedCampaignData, actionType) => {
    const res = await axios.put(`/campaign/${updatedCampaignData.id}`, updatedCampaignData);
    if (res.data?.success && res.data.data) {
      const updated = res.data.data;
      setCampaigns((prev) => {
        const exists = prev.some((c) => c.id === updated.id);
        if (exists) {
          return prev.map((c) => (c.id === updated.id ? updated : c));
        }
        return [updated, ...prev];
      });

      const firstTouchpoint =
        (updatedCampaignData.strategy?.journey && updatedCampaignData.strategy.journey[0]) ||
        (updatedCampaignData.journey && updatedCampaignData.journey[0]);
      const rawChannel = (
        firstTouchpoint?.channel ||
        (Array.isArray(updatedCampaignData.channels) &&
          updatedCampaignData.channels.find((c) => /linkedin|whatsapp|email/i.test(c))) ||
        'email'
      ).toLowerCase();
      const needsOutreachModal =
        actionType === 'DIRECT_ACTIVATE' &&
        (rawChannel.includes('email') ||
          rawChannel.includes('whatsapp') ||
          rawChannel.includes('linkedin'));

      const shouldStayInCurrentTab =
        actionType === 'PAUSE' ||
        actionType === 'RESUME' ||
        actionType === 'SAVE_CHANGES' ||
        actionType === 'REQUEST_PAUSE' ||
        actionType === 'REJECT_PAUSE_REQUEST';

      if (shouldStayInCurrentTab || needsOutreachModal) {
        setSelectedCampaignForDetail(updated);
      } else {
        setSelectedCampaignForDetail(null);
        setViewMode('LIST');
      }

      if (actionType === 'REQUEST_PAUSE') {
        setSuccessBannerMessage(
          `Pause approval request for "${updated.name}" submitted. Awaiting manager/owner approval.`
        );
      } else if (actionType === 'REJECT_PAUSE_REQUEST') {
        setSuccessBannerMessage(
          `Pause request for "${updated.name}" was rejected. Campaign remains active.`
        );
      } else {
        setSuccessBannerMessage(`Campaign "${updated.name}" successfully updated in database.`);
      }
      setTimeout(() => setSuccessBannerMessage(''), 6000);
      return updated;
    } else {
      throw new Error(res.data?.error || res.data?.message || 'Failed to update campaign');
    }
  };

  const handleCampaignDispatchedUpdate = (updatedCampaign) => {
    if (!updatedCampaign || !updatedCampaign.id) return;
    setCampaigns((prev) => {
      const exists = prev.some((c) => c.id === updatedCampaign.id);
      if (exists) {
        return prev.map((c) => (c.id === updatedCampaign.id ? { ...c, ...updatedCampaign } : c));
      }
      return [updatedCampaign, ...prev];
    });
    if (selectedCampaignForDetail?.id === updatedCampaign.id) {
      setSelectedCampaignForDetail((prev) => ({ ...prev, ...updatedCampaign }));
    }
  };

  const handleDeleteCampaign = async (id, e) => {
    if (e) e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this campaign?')) {
      try {
        await axios.delete(`/campaign/${id}`);
        setCampaigns((prev) => prev.filter((c) => c.id !== id));
        if (selectedCampaignForDetail?.id === id) {
          setViewMode('LIST');
          setSelectedCampaignForDetail(null);
        }
        setSuccessBannerMessage('Campaign successfully deleted from database.');
        setTimeout(() => setSuccessBannerMessage(''), 5000);
      } catch (err) {
        console.error('[Campaign] Error deleting campaign:', err);
        alert(err.response?.data?.error || err.message || 'Failed to delete campaign');
      }
    }
  };

  const handleSeedSampleCampaigns = async () => {
    setIsSeedingSamples(true);
    try {
      const res = await axios.post('/campaign/seed-samples');
      if (res.data?.success && Array.isArray(res.data.data)) {
        setCampaigns(res.data.data);
        setSuccessBannerMessage('Demo campaigns loaded into database for your workspace!');
        setTimeout(() => setSuccessBannerMessage(''), 6000);
      }
    } catch (err) {
      console.error('[Campaign] Failed to seed demo campaigns:', err);
      alert(err.response?.data?.error || err.message || 'Failed to load demo campaigns');
    } finally {
      setIsSeedingSamples(false);
    }
  };

  // ══════════════════════════════════════════════════════════════════
  // VIEW MODE: CREATE CAMPAIGN STRATEGY
  // ══════════════════════════════════════════════════════════════════
  if (viewMode === 'CREATE') {
    return (
      <div className="campaign-container">
        <Banner
          title="Campaign Strategy Generator"
          subtitle="Plan, build, and orchestrate AI-powered B2B outbound sales campaigns."
          type="page-header"
        />

        <CampaignRoleBanner userRoleLabel={userRoleLabel} roleAccessText={roleAccessText} />

        <CreateCampaign
          workspaceName={workspaceName || 'Primary Workspace'}
          userRoleLabel={userRoleLabel}
          currentUserName={currentUserName}
          canDirectApprove={canDirectApprove}
          initialPlaybook={selectedPlaybook}
          onCancel={() => {
            setSelectedPlaybook(null);
            setViewMode('LIST');
          }}
          onSaveCampaign={handleCreateCampaign}
          onCampaignDispatchedUpdate={handleCampaignDispatchedUpdate}
        />
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════
  // VIEW MODE: DETAIL & TAB-BASED MODIFICATION INSPECTOR
  // ══════════════════════════════════════════════════════════════════
  if (viewMode === 'DETAIL' && selectedCampaignForDetail) {
    return (
      <div className="campaign-container">
        <Banner
          title="Campaign Strategy Generator"
          subtitle="Inspect, modify, and manage multi-gate governance approvals across all campaign dimensions."
          type="page-header"
        />

        <CampaignRoleBanner userRoleLabel={userRoleLabel} roleAccessText={roleAccessText} />

        <CreateCampaign
          workspaceName={workspaceName || 'Primary Workspace'}
          userRoleLabel={userRoleLabel}
          currentUserName={currentUserName}
          canDirectApprove={canDirectApprove}
          initialCampaign={selectedCampaignForDetail}
          initialStep={initialWizardStep}
          mode="EDIT"
          onCancel={() => {
            setSelectedCampaignForDetail(null);
            setViewMode('LIST');
          }}
          onSaveCampaign={handleUpdateCampaign}
          onCampaignDispatchedUpdate={handleCampaignDispatchedUpdate}
        />
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════
  // VIEW MODE: LIST (CAMPAIGNS CATALOG & MANAGEMENT)
  // ══════════════════════════════════════════════════════════════════
  return (
    <div className="cmp-main-page-wrapper">
      {successBannerMessage && (
        <div className="cmp-alert success cmp-top-success-banner" style={{ margin: '16px 24px 0' }}>
          <i className="fas fa-check-circle" />
          <div className="cmp-alert-msg-wrap">
            <strong>Success:</strong>
            <p>{successBannerMessage}</p>
          </div>
          <button
            type="button"
            className="cmp-alert-close-btn"
            onClick={() => setSuccessBannerMessage('')}
          >
            <i className="fas fa-times" />
          </button>
        </div>
      )}

      {loadError && (
        <div className="cmp-alert danger cmp-top-error-banner" style={{ margin: '16px 24px 0' }}>
          <i className="fas fa-exclamation-circle" />
          <div className="cmp-alert-msg-wrap">
            <strong>Database Notice:</strong>
            <p>{loadError}</p>
          </div>
          <button type="button" className="cmp-alert-close-btn" onClick={() => setLoadError('')}>
            <i className="fas fa-times" />
          </button>
        </div>
      )}

      {isLoadingCampaigns && (
        <div style={{ padding: '12px 24px', fontSize: '13px', color: '#64748b' }}>
          <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }} />
          Loading workspace campaigns from database...
        </div>
      )}

      <CampaignListView
        userRoleLabel={userRoleLabel}
        roleAccessText={roleAccessText}
        campaigns={userVisibleCampaigns}
        filteredCampaigns={filteredCampaigns}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedStatus={selectedStatus}
        onSelectStatus={setSelectedStatus}
        statusCounts={statusCounts}
        showBlueprints={showBlueprints}
        onToggleBlueprints={() => setShowBlueprints((prev) => !prev)}
        onCloseBlueprints={() => setShowBlueprints(false)}
        onSelectPlaybook={(playbook) => {
          setSelectedPlaybook(playbook);
          setShowBlueprints(false);
          setViewMode('CREATE');
        }}
        onCreateCampaign={() => {
          setSelectedPlaybook(null);
          setShowBlueprints(false);
          setViewMode('CREATE');
        }}
        onSelectCampaign={(camp, targetStep = 1) => {
          setSelectedCampaignForDetail(camp);
          setInitialWizardStep(targetStep);
          setViewMode('DETAIL');
        }}
        onDeleteCampaign={handleDeleteCampaign}
        onSeedSampleCampaigns={handleSeedSampleCampaigns}
        isSeeding={isSeedingSamples}
      />
    </div>
  );
}
